import { ToastType } from '@components/toast';
import { DocumentGenerationPostRequestBody, DocumentGenerationPostResponse } from '@lio/api-types';
import { PackageTypeId } from '@models/document';
import { documentQueryKey } from '@services/document/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useCreateDocumentGeneration = () => {
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const client = useClient();
  const result = useMutation({
    mutationFn: (body: DocumentGenerationPostRequestBody) => {
      const isLossHistoryReport = body.packageTypeId === PackageTypeId.LossHistoryReport;
      if (isLossHistoryReport) {
        enqueueSnackbar('Loss History Report generation started', {
          variant: ToastType.Success,
          autoHideDuration: 10000,
        });
      }

      return client
        .post('document/document-generation', {
          json: {
            ...body,
          },
        })
        .json<DocumentGenerationPostResponse>();
    },
    onSuccess: (res, body) => {
      if (res.status === 'Completed') {
        /** If res has a downloadUrl, the document was already generated */
        if (res?.downloadUrl) {
          enqueueSnackbar('Document already generated', {
            variant: ToastType.Info,
            persist: true,
          });
        } else {
          /* Document generation is async. Getting status 'Completed' back immediately
        means that no work was queued because no forms were found to generate */
          enqueueSnackbar('No document was generated', {
            variant: ToastType.Error,
            persist: true,
          });
        }
      } else if (body.packageTypeId !== PackageTypeId.LossHistoryReport) {
        // Show success message for async document generation
        enqueueSnackbar('Document generation started successfully', {
          variant: ToastType.Success,
          autoHideDuration: 5000,
        });
      }

      queryClient.invalidateQueries({ queryKey: documentQueryKey.search });
    },
    onError: async (e) => {
      const message = await getApiErrorMessage(e, 'Failed to start document generation');
      enqueueSnackbar(message, {
        variant: ToastType.Error,
        persist: true,
      });
    },
  });
  return { ...result, createDocumentGenerationAsync: result.mutateAsync };
};
